import React, { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { createPortal } from "react-dom";
import {
  ArrowLeftOutlined,
  ArrowDownOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  DownloadOutlined,
  FileOutlined,
  LoadingOutlined,
  MessageOutlined,
  PaperClipOutlined,
  PlusOutlined,
  ReloadOutlined,
  RollbackOutlined,
  SearchOutlined,
  SendOutlined,
  TeamOutlined,
  WarningFilled,
  SmileOutlined,
  ExpandOutlined,
} from "@ant-design/icons";
import { Avatar, Badge, Button, Empty, Input, Popover, Progress, Select, Spin, Tooltip, message as antMessage } from "antd";
import { useAuth } from "../contexts/AuthContext";
import { StaffAvatar } from "../staff/StaffAvatar";
import { useInternalChat } from "./InternalChatContext";
import { INTERNAL_CHAT_PRIORITY, lastMessagePreview, userDisplayName } from "./internalChatModel";
import InternalAttachmentReviewModal from "./InternalAttachmentReviewModal";
import { ImageEditorModal } from "../qv4/ImageEditorModal";
import "./internalChat.css";
import ScrollEdgeFrame from '../shared/ScrollEdgeFrame';
import AppTheme from '../theme/AppTheme';
import './internal-chat-theme.css';
import { useAppearance } from '../contexts/AppearanceContext';
import { ChatMediaPreview } from '../qv4/views/chat/ChatMediaPreview';
import { BUBBLE_MAX_WIDTH } from '../qv4/views/chat/chatLayout';
const InternalChatGallery = React.lazy(() => import('./InternalChatGallery'));
const InternalEmojiPicker = React.lazy(() => import('emoji-picker-react'));

const { TextArea } = Input;
const FILE_ACCEPT = ".jpg,.jpeg,.png,.gif,.webp,.heic,.heif,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.vcf";
const MAX_PENDING_FILES = 10;
const IMAGE_MAX_BYTES = 15 * 1024 * 1024;
const DOCUMENT_MAX_BYTES = 50 * 1024 * 1024;
const IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/gif", "image/webp", "image/heic", "image/heif"]);
const DOCUMENT_TYPES = new Set([
  "application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint", "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "text/plain", "text/csv", "text/vcard", "text/x-vcard",
]);

function formatListTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const now = new Date();
  if (date.toDateString() === now.toDateString()) return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return date.toLocaleDateString([], { month: "short", day: "numeric" });
}

function dateParts(value) {
  const date = new Date(value || 0);
  if (Number.isNaN(date.getTime())) return null;
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  const label = date.toDateString() === today.toDateString()
    ? "Today"
    : date.toDateString() === yesterday.toDateString()
      ? "Yesterday"
      : date.toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" });
  return { key, label };
}

function makePendingId() {
  return window.crypto?.randomUUID?.() || `file-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function dataUrlToFile(dataUrl, filename, mimeType) {
  const [, base64] = dataUrl.split(",");
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return new File([bytes], filename, { type: mimeType });
}

function formatPresence(user) {
  if (user?.is_online) return "Online";
  const value = user?.last_seen_at || user?.internal_chat_last_seen_at;
  if (!value) return "Offline";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Offline";
  return `Last seen ${date.toLocaleDateString([], { month: "short", day: "numeric" })} at ${date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
}

function UserAvatar({ user, size = 38, system = false }) {
  const name = system ? "Uzdpart360" : userDisplayName(user);
  return (
    <span className="internal-chat-avatar-wrap">
      {system ? <Avatar size={size} className="is-system">S</Avatar> :
        <StaffAvatar size={size} userId={user?.id ?? user?.user_id} pfp={user?.pfp} name={name} />}
      {!system && <span className={`internal-chat-presence-dot ${user?.is_online ? "is-online" : ""}`} />}
    </span>
  );
}

function PriorityPill({ priority, compact = false }) {
  const item = INTERNAL_CHAT_PRIORITY[Number(priority) || 3];
  if (compact && Number(priority) < 4) return null;
  return (
    <span className="internal-chat-priority" style={{ color: item.color, background: item.background }}>
      {Number(priority) === 5 && <WarningFilled />}{item.label}
    </span>
  );
}

function SecureImage({ message, resolveDownloadUrl }) {
  const [src, setSrc] = useState(message._local_url || null);
  useEffect(() => {
    let active = true;
    if (!message.file?.id || message._local_url) return undefined;
    resolveDownloadUrl(message.file.id).then(url => { if (active) setSrc(url); }).catch(() => {});
    return () => { active = false; };
  }, [message._local_url, message.file?.id, resolveDownloadUrl]);
  return (
    <div className="internal-chat-media-block">
      <ChatMediaPreview kind="image" src={src} alt={message.file?.original_name || 'Shared image'} placeholder={<Spin size="small" />} rowSelector=".internal-chat-message-row" previewZIndex={1600} />
    </div>
  );
}

function Receipt({ message }) {
  if (message._status === "failed") return <span className="internal-chat-receipt is-failed">Failed</span>;
  if (message._status === "queued") return <span className="internal-chat-receipt is-queued">Queued</span>;
  if (message._status === "uploading") return <span className="internal-chat-receipt"><LoadingOutlined /> {message._upload_progress || 0}%</span>;
  if (message._status === "sending" || String(message.id).startsWith("local:")) return <ClockCircleOutlined className="internal-chat-receipt" />;
  if (message.read_at) return <Tooltip title="Read"><span className="internal-chat-receipt is-read" aria-label="Read"><b>✓✓</b></span></Tooltip>;
  if (message.delivered_at) return <Tooltip title="Delivered"><span className="internal-chat-receipt is-delivered" aria-label="Delivered"><b>✓✓</b></span></Tooltip>;
  return <Tooltip title="Sent"><span className="internal-chat-receipt is-sent" aria-label="Sent"><b>✓</b></span></Tooltip>;
}

function MessageBubble({ message, mine, highlighted, onReply, onRetry, onDownload, resolveDownloadUrl }) {
  if (message.sender_type === "system") {
    return (
      <div className="internal-chat-system-message">
        <span>{message.text_content || "System notification"}</span>
        <time>{formatListTime(message.created_at)}</time>
      </div>
    );
  }
  const quote = message.quoted_message;
  return (
    <div data-internal-message-id={message.id} className={`internal-chat-message-row ${mine ? "is-mine" : ""} ${highlighted ? 'is-jump-target' : ''}`}>
      <div className={`internal-chat-bubble ${['image', 'document'].includes(message.message_type) ? 'internal-chat-bubble--media' : ''} ${mine ? "is-mine" : ""} ${message._status === "failed" ? "is-failed" : ""}`} style={{ maxWidth: `min(80%, ${BUBBLE_MAX_WIDTH}px)`, ...(message.message_type === 'document' ? { width: BUBBLE_MAX_WIDTH } : {}) }}>
        <div className="internal-chat-bubble-top">
          <PriorityPill priority={message.priority} compact />
        </div>
        <div className="internal-chat-bubble-actions">
          <Tooltip title="Quote"><button type="button" aria-label="Quote message" disabled={String(message.id).startsWith('local:')} onClick={() => onReply(message)}><RollbackOutlined /></button></Tooltip>
          {message.file && <Tooltip title="Download"><button type="button" aria-label="Download attachment" onClick={() => onDownload(message.file, message._pending_file)}><DownloadOutlined /></button></Tooltip>}
        </div>
        {quote && (
          <div className="internal-chat-quote">
            <strong>{quote.sender_type === "system" ? "Uzdpart360 System" : "Quoted message"}</strong>
            <span>{quote.text_content || (quote.message_type === "image" ? "Image" : "Document")}</span>
          </div>
        )}
        {message.message_type === "image" && <SecureImage message={message} resolveDownloadUrl={resolveDownloadUrl} onDownload={onDownload} onReply={() => onReply(message)} />}
        {message.message_type === "document" && (
          <div className="internal-chat-media-block" style={{ width: '100%' }}>
            <div className="internal-chat-document" style={{ width: '100%' }}>
              <span className="internal-chat-document-icon"><FileOutlined /></span>
              <span><strong>{message.file?.original_name || "Document"}</strong><small>{message.file?.mime_type || "File attachment"}</small></span>
            </div>
          </div>
        )}
        {message.text_content && <div className="internal-chat-message-text">{message.text_content}</div>}
        {message._status === "uploading" && <Progress percent={message._upload_progress || 0} size="small" showInfo={false} />}
        <div className="internal-chat-message-meta">
          <time>{formatListTime(message.created_at)}</time>
          {mine && <Receipt message={message} />}
          {message._status === "failed" && <button type="button" onClick={() => onRetry(message)}><ReloadOutlined /> Retry</button>}
        </div>
      </div>
    </div>
  );
}

function ConversationList({ conversations, selectedId, search, setSearch, onSelect, onDirectory, syncing }) {
  const { authState } = useAuth();
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return conversations;
    return conversations.filter(row => `${row.type === "system" ? "Uzdpart360 System" : userDisplayName(row.other_user)} ${lastMessagePreview(row.last_message)}`.toLowerCase().includes(term));
  }, [conversations, search]);
  return (
    <>
      <div className="internal-chat-list-tools">
        <Input allowClear value={search} onChange={event => setSearch(event.target.value)} prefix={<SearchOutlined />} placeholder="Search conversations" />
        <Tooltip title="New conversation"><Button type="primary" icon={<PlusOutlined />} onClick={onDirectory} /></Tooltip>
      </div>
      {syncing && <div className="internal-chat-sync-strip"><LoadingOutlined /> Syncing missed messages…</div>}
      <ScrollEdgeFrame style={{ flex: 1, minHeight: 0 }}><div data-scroll-edge-viewport className="internal-chat-list-scroll">
        {filtered.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No conversations yet" /> : filtered.map(row => {
          const system = row.type === "system";
          const name = system ? (row.system?.name || "Uzdpart360 System") : userDisplayName(row.other_user);
          const preview = lastMessagePreview(row.last_message);
          const mediaPreview = ['image', 'video', 'audio', 'voice', 'document', 'sticker'].includes(row.last_message?.message_type);
          return (
            <button type="button" key={row.id} className={`internal-chat-conversation ${String(selectedId) === String(row.id) ? "is-selected" : ""}`} onClick={() => onSelect(row.id)}>
              <UserAvatar user={row.other_user} system={system} />
              <span className="internal-chat-conversation-copy">
                <span className="internal-chat-conversation-line"><strong>{name}</strong><time>{formatListTime(row.last_message_at || row.last_message?.created_at)}</time></span>
                <span className="internal-chat-conversation-line is-preview">
                  {!system && Number(row.last_message?.sender_user_id) === Number(authState?.id) && <Receipt message={row.last_message} />}
                  <span className="internal-chat-preview-content">{mediaPreview ? <><span className="internal-chat-preview-icon">{preview.slice(0, preview.indexOf(' '))}</span><span className="internal-chat-preview-text">{preview.slice(preview.indexOf(' ') + 1)}</span></> : <span className="internal-chat-preview-text">{preview}</span>}</span>
                  {Number(row.unread_count) > 0 && <Badge count={row.unread_count} overflowCount={99} />}
                </span>
              </span>
            </button>
          );
        })}
      </div></ScrollEdgeFrame>
    </>
  );
}

function UserDirectory({ users, search, setSearch, onBack, onSelect }) {
  const [startingId, setStartingId] = useState(null);
  const filtered = users.filter(user => userDisplayName(user).toLowerCase().includes(search.trim().toLowerCase()));
  const choose = async user => {
    setStartingId(user.id);
    try { await onSelect(user); } finally { setStartingId(null); }
  };
  return (
    <>
      <div className="internal-chat-directory-title"><Button type="text" icon={<ArrowLeftOutlined />} onClick={onBack} /><span><strong>New conversation</strong><small>Enabled Uzdpart360 users</small></span></div>
      <div className="internal-chat-list-tools is-single"><Input allowClear autoFocus value={search} onChange={event => setSearch(event.target.value)} prefix={<SearchOutlined />} placeholder="Find a colleague" /></div>
      <ScrollEdgeFrame style={{ flex: 1, minHeight: 0 }}><div data-scroll-edge-viewport className="internal-chat-list-scroll">
        {filtered.map(user => (
          <button type="button" key={user.id} className="internal-chat-conversation" onClick={() => choose(user)} disabled={startingId !== null}>
            <UserAvatar user={user} />
            <span className="internal-chat-conversation-copy"><strong>{userDisplayName(user)}</strong><small className={user.is_online ? "is-online-text" : ""}>{formatPresence(user)}</small></span>
            {Number(startingId) === Number(user.id) && <Spin size="small" />}
          </button>
        ))}
        {filtered.length === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No enabled users found" />}
      </div></ScrollEdgeFrame>
    </>
  );
}

function ConversationPane({ conversation, messages, meta, currentUserId, loadOlderMessages, stageMessage, sendMessage, retryMessage, resolveDownloadUrl, onMobileBack, onClose }) {
  const { composer, setComposer } = useInternalChat();
  const { drafts, priorities, replies, pendingByConversation } = composer;
  const updateComposer = key => updater => setComposer(previous => ({ ...previous, [key]: typeof updater === 'function' ? updater(previous[key]) : updater }));
  const setDrafts = updateComposer('drafts');
  const setPriorities = updateComposer('priorities');
  const setReplies = updateComposer('replies');
  const setPendingByConversation = updateComposer('pendingByConversation');
  const { isDark } = useAppearance();
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [jumpTarget, setJumpTarget] = useState(null);
  const inputRef = useRef(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewActiveId, setReviewActiveId] = useState(null);
  const [editingFileId, setEditingFileId] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendingMedia, setSendingMedia] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [stickyDate, setStickyDate] = useState(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [showUnreadSeparator, setShowUnreadSeparator] = useState(false);
  const [unreadBoundaryIndex, setUnreadBoundaryIndex] = useState(null);
  const scrollRef = useRef(null);
  const messagesEndRef = useRef(null);
  const fileRef = useRef(null);
  const previousScrollTopRef = useRef(0);
  const messageCountRef = useRef(0);
  const previousMessageKeysRef = useRef(new Set());
  const previousFirstMessageKeyRef = useRef(null);
  const openUnreadCountRef = useRef(0);
  const historyInitializedRef = useRef(false);
  const id = conversation?.id;
  useEffect(() => { setJumpTarget(null); }, [id]);
  useEffect(() => {
    if (galleryOpen || jumpTarget === null) return;
    const frame = requestAnimationFrame(() => {
      const scroller = scrollRef.current;
      const target = Array.from(scroller?.querySelectorAll('[data-internal-message-id]') || [])
        .find(row => row.dataset.internalMessageId === String(jumpTarget));
      if (target) {
        scroller.scrollTop += target.getBoundingClientRect().top - scroller.getBoundingClientRect().top
          - (scroller.clientHeight - target.getBoundingClientRect().height) / 2;
      }
    });
    const timer = setTimeout(() => setJumpTarget(null), 2000);
    return () => { cancelAnimationFrame(frame); clearTimeout(timer); };
  }, [galleryOpen, jumpTarget]);
  const draft = drafts[id] || "";
  const priority = priorities[id] || 3;
  const reply = replies[id] || null;
  const pendingFiles = pendingByConversation[id] || [];
  const system = conversation?.type === "system";

  const updateStickyDate = () => {
    const element = scrollRef.current;
    if (!element) return;
    let current = null;
    element.querySelectorAll("[data-date-label]").forEach(separator => {
      if (separator.offsetTop <= element.scrollTop + 48) current = separator.dataset.dateLabel;
    });
    setStickyDate(current);
  };

  useEffect(() => {
    setStickyDate(null);
    setShowScrollButton(false);
    setReviewOpen(false);
    setEmojiOpen(false);
    setGalleryOpen(false);
    previousScrollTopRef.current = 0;
    previousMessageKeysRef.current = new Set();
    previousFirstMessageKeyRef.current = null;
    messageCountRef.current = 0;
    openUnreadCountRef.current = Number(conversation?.unread_count || 0);
    historyInitializedRef.current = false;
    setUnreadBoundaryIndex(null);
    setShowUnreadSeparator(openUnreadCountRef.current > 0);
    const element = scrollRef.current;
    if (element) requestAnimationFrame(() => { element.scrollTop = element.scrollHeight; updateStickyDate(); });
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return;
    const previousCount = messageCountRef.current;
    const previousKeys = previousMessageKeysRef.current;
    const initialBatch = !historyInitializedRef.current;
    const keyOf = item => item.local_message_id || `id:${item.id}`;
    const additions = messages.filter(item => !previousKeys.has(keyOf(item)));
    const previousFirstIndex = previousFirstMessageKeyRef.current
      ? messages.findIndex(item => keyOf(item) === previousFirstMessageKeyRef.current)
      : -1;
    const historyPrepended = previousCount > 0 && previousFirstIndex > 0
      && messages.slice(0, previousFirstIndex).every(item => !previousKeys.has(keyOf(item)));
    if (unreadBoundaryIndex === null && openUnreadCountRef.current > 0 && messages.length) {
      setUnreadBoundaryIndex(Math.max(0, messages.length - openUnreadCountRef.current));
      openUnreadCountRef.current = 0;
      setShowUnreadSeparator(true);
    } else if (!initialBatch && !historyPrepended && unreadBoundaryIndex === null && additions.some(item => item.sender_type === "system" || Number(item.sender_user_id) !== Number(currentUserId))) {
      const isNearBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 150;
      const tabActive = !document.hidden && document.hasFocus();
      if (!(isNearBottom && tabActive)) {
        setUnreadBoundaryIndex(previousCount);
        setShowUnreadSeparator(true);
      }
    }
    messageCountRef.current = messages.length;
    previousMessageKeysRef.current = new Set(messages.map(keyOf));
    previousFirstMessageKeyRef.current = messages.length ? keyOf(messages[0]) : null;
    if (messages.length || meta.initialized) historyInitializedRef.current = true;
    const nearBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 180;
    if (nearBottom) requestAnimationFrame(() => { element.scrollTop = element.scrollHeight; updateStickyDate(); });
    else if (additions.length) setShowScrollButton(true);
  }, [messages.length, meta.initialized, unreadBoundaryIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!conversation) return <div className="internal-chat-empty"><div><MessageOutlined /><h3>Your internal conversations</h3><p>Select a colleague to start chatting.</p></div></div>;

  const setPendingFiles = updater => setPendingByConversation(previous => {
    const current = previous[id] || [];
    const next = typeof updater === "function" ? updater(current) : updater;
    return { ...previous, [id]: next };
  });
  const addFiles = fileList => {
    const accepted = [];
    const rejected = [];
    const slots = Math.max(0, MAX_PENDING_FILES - pendingFiles.length);
    Array.from(fileList || []).forEach(file => {
      const isImage = IMAGE_TYPES.has(file.type);
      const isDocument = DOCUMENT_TYPES.has(file.type);
      const maximum = isImage ? IMAGE_MAX_BYTES : DOCUMENT_MAX_BYTES;
      if (!isImage && !isDocument) rejected.push(`${file.name} — unsupported type`);
      else if (file.size > maximum) rejected.push(`${file.name} — exceeds ${isImage ? "15 MB image" : "50 MB document"} limit`);
      else if (accepted.length >= slots) rejected.push(`${file.name} — maximum ${MAX_PENDING_FILES} files at once`);
      else accepted.push(file);
    });
    if (rejected.length) antMessage.warning({ content: <span>{rejected.length} file{rejected.length > 1 ? "s" : ""} rejected<div style={{ whiteSpace: "pre-line", textAlign: "left" }}>{rejected.join("\n")}</div></span>, duration: 7 });
    if (!accepted.length) return;
    const entries = accepted.map(file => ({ id: makePendingId(), file, caption: "", previewUrl: file.type.startsWith("image/") ? URL.createObjectURL(file) : null }));
    setPendingFiles(current => [...current, ...entries]);
    setReviewActiveId(current => current || entries[0].id);
    setReviewOpen(true);
  };
  const removeFile = fileId => setPendingFiles(current => {
    const entry = current.find(item => item.id === fileId);
    if (entry?.previewUrl) URL.revokeObjectURL(entry.previewUrl);
    const next = current.filter(item => item.id !== fileId);
    if (!next.length) setReviewOpen(false);
    return next;
  });
  const discardFiles = () => {
    pendingFiles.forEach(entry => { if (entry.previewUrl) URL.revokeObjectURL(entry.previewUrl); });
    setPendingFiles([]);
    setReviewOpen(false);
    setReviewActiveId(null);
  };
  const updateCaption = (fileId, caption) => setPendingFiles(current => current.map(entry => entry.id === fileId ? { ...entry, caption } : entry));
  const updateFileContent = (fileId, nextFile) => setPendingFiles(current => current.map(entry => {
    if (entry.id !== fileId) return entry;
    if (entry.previewUrl) URL.revokeObjectURL(entry.previewUrl);
    return { ...entry, file: nextFile, previewUrl: URL.createObjectURL(nextFile) };
  }));
  const sendText = async () => {
    if (sending || !draft.trim()) return;
    setSending(true);
    setShowUnreadSeparator(false);
    setUnreadBoundaryIndex(null);
    const result = await sendMessage({ conversationId: id, text: draft, priority, quotedMessage: reply });
    setSending(false);
    if (result) {
      setDrafts(previous => ({ ...previous, [id]: "" }));
      setReplies(previous => ({ ...previous, [id]: null }));
    }
  };
  const sendFiles = async () => {
    if (!pendingFiles.length || sendingMedia) return;
    const snapshot = [...pendingFiles];
    setSendingMedia(true);
    setShowUnreadSeparator(false);
    setUnreadBoundaryIndex(null);
    setReviewOpen(false);
    setPendingFiles([]);
    const batchStartedAt = Date.now();
    const staged = snapshot.map((entry, index) => ({
      entry,
      localMessageId: stageMessage({ conversationId: id, text: entry.caption, priority, quotedMessage: reply, file: entry.file, queued: true, createdAt: new Date(batchStartedAt + index).toISOString() })?.localMessageId,
    }));
    requestAnimationFrame(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; });
    for (const item of staged) {
      if (!item.localMessageId) continue;
      // eslint-disable-next-line no-await-in-loop
      await sendMessage({ conversationId: id, text: item.entry.caption, priority, quotedMessage: reply, file: item.entry.file, localMessageId: item.localMessageId });
      if (item.entry.previewUrl) URL.revokeObjectURL(item.entry.previewUrl);
    }
    setReplies(previous => ({ ...previous, [id]: null }));
    setReviewActiveId(null);
    setSendingMedia(false);
  };
  const triggerDownload = (url, filename) => {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename || "download";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  };
  const download = async (attachment, localFile, knownUrl) => {
    if (localFile) {
      const url = URL.createObjectURL(localFile);
      triggerDownload(url, localFile.name);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      return;
    }
    if (!attachment?.id && !knownUrl) return;
    const url = knownUrl || await resolveDownloadUrl(attachment.id);
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error("Download failed");
      const blobUrl = URL.createObjectURL(await response.blob());
      triggerDownload(blobUrl, attachment?.original_name);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (_) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };
  const other = conversation.other_user;
  let previousDateKey = null;
  const visibleUnreadBoundaryIndex = showUnreadSeparator && unreadBoundaryIndex !== null ? unreadBoundaryIndex : -1;
  const renderedMessages = messages.flatMap((chatMessage, messageIndex) => {
    const elements = [];
    const date = dateParts(chatMessage.created_at);
    if (date && date.key !== previousDateKey) {
      previousDateKey = date.key;
      elements.push(<div key={`date-${date.key}`} data-date-label={date.label} className="internal-chat-date-separator"><i /><span>{date.label}</span><i /></div>);
    }
    if (messageIndex === visibleUnreadBoundaryIndex) {
      elements.push(<div key="new-messages" className="internal-chat-unread-separator"><i /><span>{messages.length - visibleUnreadBoundaryIndex} new message{messages.length - visibleUnreadBoundaryIndex === 1 ? "" : "s"}</span><i /></div>);
    }
    elements.push(<MessageBubble key={chatMessage.id || chatMessage.local_message_id} message={chatMessage} highlighted={String(jumpTarget) === String(chatMessage.id)} mine={Number(chatMessage.sender_user_id) === Number(currentUserId)} onReply={item => setReplies(previous => ({ ...previous, [id]: item }))} onRetry={retryMessage} onDownload={download} resolveDownloadUrl={resolveDownloadUrl} />);
    return elements;
  });

  return (
    <div className="internal-chat-thread">
      <div className="internal-chat-thread-header">
        <Button type="text" aria-label={galleryOpen ? 'Back to messages' : 'Back to conversations'} className={galleryOpen ? '' : 'internal-chat-mobile-back'} icon={<ArrowLeftOutlined />} onClick={() => galleryOpen ? setGalleryOpen(false) : onMobileBack()} />
        <button type="button" className="internal-chat-gallery-heading" aria-label={galleryOpen ? 'Shared media' : 'Open shared media'} onClick={() => setGalleryOpen(true)}>
          <UserAvatar user={other} system={system} size={36} />
          <span className="internal-chat-thread-heading"><span className="internal-chat-thread-name"><strong>{system ? (conversation.system?.name || "Uzdpart360 System") : userDisplayName(other)}</strong>{!system && <PriorityPill priority={priority} />}</span><small className={other?.is_online ? "is-online-text" : ""}>{galleryOpen ? 'Shared media' : system ? "System notifications" : formatPresence(other)}</small></span>
        </button>
        <Tooltip title="Close chat"><Button type="text" aria-label="Close conversation" icon={<CloseOutlined />} onClick={onClose} /></Tooltip>
      </div>
      {galleryOpen && <Suspense fallback={<Spin />}><InternalChatGallery messages={messages} meta={meta} loadOlderMessages={loadOlderMessages} resolveDownloadUrl={resolveDownloadUrl} onDownload={download} onSeeInChat={messageId => { setJumpTarget(messageId); setGalleryOpen(false); }} /></Suspense>}
      <div className="internal-chat-thread-body" style={{ display: galleryOpen ? 'none' : 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <div
        className="internal-chat-message-stage"
        onDragOver={event => { if (!system) { event.preventDefault(); setDragOver(true); } }}
        onDragLeave={event => { if (!event.currentTarget.contains(event.relatedTarget)) setDragOver(false); }}
        onDrop={event => { if (!system) { event.preventDefault(); setDragOver(false); addFiles(event.dataTransfer.files); } }}
      >
        {dragOver && <div className="internal-chat-drop-overlay"><PaperClipOutlined /><strong>Drop files to attach</strong><span>Images and documents</span></div>}
        <div className="internal-chat-messages" ref={scrollRef} onScroll={event => {
          const element = event.currentTarget;
          const current = element.scrollTop;
          const scrollingDown = current > previousScrollTopRef.current;
          previousScrollTopRef.current = current;
          const atBottom = element.scrollHeight - current - element.clientHeight < 100;
          if (atBottom) setShowScrollButton(false);
          else if (scrollingDown) setShowScrollButton(true);
          else setShowScrollButton(false);
          updateStickyDate();
        }}>
          {meta.hasMore && <Button className="internal-chat-load-older" size="small" loading={meta.loadingOlder} onClick={loadOlderMessages}>Load older messages</Button>}
          {meta.loading && messages.length === 0 ? <div className="internal-chat-loading"><Spin /></div> : renderedMessages}
          <div ref={messagesEndRef} />
        </div>
        {stickyDate && <div className="internal-chat-sticky-date"><span>{stickyDate}</span></div>}
        {showScrollButton && <button type="button" className="internal-chat-scroll-bottom" aria-label="Scroll to latest messages" onClick={() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); setShowScrollButton(false); }}><ArrowDownOutlined /></button>}
      </div>
      {system ? <div className="internal-chat-readonly"><WarningFilled /> This is a read-only system conversation.</div> : (
        <div className="internal-chat-composer-wrap">
          {reply && <div className="internal-chat-compose-quote"><span><strong>Replying to message</strong><small>{reply.text_content || (reply.message_type === "image" ? "Image" : "Document")}</small></span><Button type="text" size="small" icon={<CloseOutlined />} onClick={() => setReplies(previous => ({ ...previous, [id]: null }))} /></div>}
          <div className="internal-chat-composer">
            <input ref={fileRef} type="file" multiple accept={FILE_ACCEPT} hidden onChange={event => { addFiles(event.target.files); event.target.value = ""; }} />
            <Tooltip title="Attach images or documents"><Button type="text" icon={<PaperClipOutlined />} onClick={() => fileRef.current?.click()} /></Tooltip>
            <Popover zIndex={1500} trigger="click" open={emojiOpen} onOpenChange={setEmojiOpen} placement="topLeft" content={<Suspense fallback={<Spin />}>{emojiOpen && <InternalEmojiPicker theme={isDark ? 'dark' : 'light'} emojiStyle="native" suggestedEmojisMode="recent" width={320} height={360} previewConfig={{ showPreview: false }} onEmojiClick={({ emoji }) => {
              const field = inputRef.current?.resizableTextArea?.textArea;
              const start = field?.selectionStart ?? draft.length;
              const end = field?.selectionEnd ?? start;
              setDrafts(previous => ({ ...previous, [id]: draft.slice(0, start) + emoji + draft.slice(end) }));
              requestAnimationFrame(() => { field?.focus(); field?.setSelectionRange(start + emoji.length, start + emoji.length); });
            }} />}</Suspense>}><Button type="text" aria-label="Choose emoji" icon={<SmileOutlined />} /></Popover>
            <TextArea ref={inputRef} autoSize={{ minRows: 1, maxRows: 4 }} value={draft} placeholder="Write a message…" onChange={event => setDrafts(previous => ({ ...previous, [id]: event.target.value }))} onKeyDown={event => { if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); sendText(); } }} />
            <Select className="internal-chat-priority-select" value={priority} size="small" popupMatchSelectWidth={false} popupClassName="internal-chat-priority-popup" dropdownStyle={{ minWidth: 106, zIndex: 1350 }} onChange={value => setPriorities(previous => ({ ...previous, [id]: value }))} options={Object.entries(INTERNAL_CHAT_PRIORITY).map(([value, item]) => ({ value: Number(value), label: item.label }))} />
            <Button type="primary" shape="circle" icon={sending ? <LoadingOutlined /> : <SendOutlined />} onClick={sendText} disabled={sending || !draft.trim()} />
          </div>
        </div>
      )}
      <InternalAttachmentReviewModal open={reviewOpen && pendingFiles.length > 0} files={pendingFiles} recipientName={userDisplayName(other)} activeId={reviewActiveId} priorityLabel={INTERNAL_CHAT_PRIORITY[priority].label} onSetActive={setReviewActiveId} onUpdateCaption={updateCaption} onRemoveFile={removeFile} onDiscardAll={discardFiles} onSendAll={sendFiles} onAddFiles={addFiles} onEditFile={setEditingFileId} sending={sendingMedia} />
      <ImageEditorModal open={!!editingFileId} zIndex={1500} source={pendingFiles.find(entry => entry.id === editingFileId)?.previewUrl} onClose={() => setEditingFileId(null)} onSave={saved => {
        const original = pendingFiles.find(entry => entry.id === editingFileId);
        if (original && saved?.imageBase64) updateFileContent(editingFileId, dataUrlToFile(saved.imageBase64, original.file.name, saved.mimeType || original.file.type));
        setEditingFileId(null);
      }} />
      </div>
    </div>
  );
}

function InternalChatPanel({ launcherRef, fullPage = false }) {
  const navigate = useNavigate();
  const chat = useInternalChat();
  const { isOpen, setIsOpen } = chat;
  const { authState } = useAuth();
  const panelRef = useRef(null);
  const [search, setSearch] = useState("");
  const [directorySearch, setDirectorySearch] = useState("");
  const [directory, setDirectory] = useState(false);
  const [mobileList, setMobileList] = useState(!chat.selectedConversationId);
  const [position, setPosition] = useState({});

  useEffect(() => {
    const positionPanel = () => {
      if (fullPage) { setPosition({}); return; }
      const mobile = window.innerWidth <= 900;
      if (mobile) { setPosition({}); return; }
      const rect = launcherRef.current?.getBoundingClientRect();
      const width = Math.min(960, window.innerWidth - 28);
      const right = Math.max(14, window.innerWidth - (rect?.right || window.innerWidth - 14));
      setPosition({ width, height: Math.min(720, window.innerHeight - 76), top: (rect?.bottom || 56) + 8, right });
    };
    positionPanel();
    window.addEventListener("resize", positionPanel);
    return () => window.removeEventListener("resize", positionPanel);
  }, [launcherRef, fullPage]);

  useEffect(() => {
    const closeOnEscape = event => {
      if (event.key !== 'Escape' || event.defaultPrevented || !(isOpen || fullPage)) return;
      if (document.querySelector('.ant-modal-root .ant-modal-wrap:not([style*="display: none"]), .ant-image-preview-root, .ant-popover:not(.ant-popover-hidden)')) return;
      if (chat.selectedConversationId) { chat.closeConversation(); setMobileList(true); }
      else if (!fullPage) setIsOpen(false);
    };
    const closeOutside = event => {
      if (fullPage || !isOpen || event.target.closest?.(".ant-select-dropdown, .ant-modal-root, .ant-image-preview-root, .ant-popover") || panelRef.current?.contains(event.target) || launcherRef.current?.contains(event.target)) return;
      setIsOpen(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    document.addEventListener("mousedown", closeOutside);
    return () => { document.removeEventListener("keydown", closeOnEscape); document.removeEventListener("mousedown", closeOutside); };
  }, [chat, isOpen, setIsOpen, launcherRef, fullPage]);

  useEffect(() => {
    if ((chat.isOpen || fullPage) && chat.selectedConversationId) setMobileList(false);
  }, [chat.isOpen, chat.selectedConversationId, fullPage]);

  const selectConversation = id => { setDirectory(false); setMobileList(false); chat.openConversation(id); };
  const startConversation = async user => { setDirectory(false); setMobileList(false); await chat.startDirectConversation(user); };
  return (
    <div ref={panelRef} style={position} className={`internal-chat-panel ${fullPage ? 'is-full-page' : ''} ${chat.isOpen || fullPage ? "is-open" : ""} ${mobileList ? "show-mobile-list" : "show-mobile-thread"}`} aria-hidden={!(chat.isOpen || fullPage)}>
      <aside className="internal-chat-sidebar">
        <div className="internal-chat-panel-title"><span className="internal-chat-title-icon"><TeamOutlined /></span><span><strong>Internal Chat</strong><small><i className={chat.isConnected ? "is-connected" : ""} />{chat.isConnected ? "Live" : "Reconnecting"}</small></span>
          {!fullPage && <Tooltip title="Open full page"><Button type="text" aria-label="Maximize internal chat" icon={<ExpandOutlined />} onClick={() => { chat.setIsOpen(false); navigate('/internal-chat'); }} /></Tooltip>}
          <Button type="text" aria-label="Close internal chat" icon={<CloseOutlined />} onClick={() => { chat.setIsOpen(false); if (fullPage) navigate('/querysetv4'); }} />
        </div>
        {directory ? <UserDirectory users={chat.users} search={directorySearch} setSearch={setDirectorySearch} onBack={() => setDirectory(false)} onSelect={startConversation} /> : <ConversationList conversations={chat.conversations} selectedId={chat.selectedConversationId} search={search} setSearch={setSearch} onSelect={selectConversation} onDirectory={() => setDirectory(true)} syncing={chat.syncing} />}
      </aside>
      <main className="internal-chat-main">
        <ConversationPane conversation={chat.selectedConversation} messages={chat.messages} meta={chat.messageMeta} currentUserId={authState?.id} loadOlderMessages={chat.loadOlderMessages} stageMessage={chat.stageMessage} sendMessage={chat.sendMessage} retryMessage={chat.retryMessage} resolveDownloadUrl={chat.resolveDownloadUrl} onMobileBack={() => setMobileList(true)} onClose={() => { chat.closeConversation(); setMobileList(true); }} />
      </main>
    </div>
  );
}

export default function InternalChatLauncher() {
  const chat = useInternalChat();
  const location = useLocation();
  const navigate = useNavigate();
  const fullPage = location.pathname === '/internal-chat';
  const launcherRef = useRef(null);
  if (chat.enabled !== true) return null;
  return (
    <>
      <Tooltip title="Internal chat" placement="bottom">
        <button
          ref={launcherRef}
          type="button"
          className={`internal-chat-launcher ${chat.isOpen || fullPage ? "is-active" : ""}`}
          onClick={() => {
            if (typeof Notification !== "undefined" && Notification.permission === "default") {
              Promise.resolve(Notification.requestPermission()).catch(() => {});
            }
            if (fullPage) navigate('/querysetv4');
            else chat.toggleOpen();
          }}
          aria-label="Open internal chat"
          aria-expanded={chat.isOpen || fullPage}
        >
          <Badge count={chat.totalUnread} overflowCount={99} size="small" color="#e11d2e">
            <span className="internal-chat-launcher-mark" aria-hidden="true">
              <TeamOutlined className="internal-chat-launcher-team" />
              <MessageOutlined className="internal-chat-launcher-bubble" />
            </span>
          </Badge>
        </button>
      </Tooltip>
      {!fullPage && createPortal(<AppTheme><InternalChatPanel launcherRef={launcherRef} /></AppTheme>, document.body)}
    </>
  );
}

export function InternalChatPage() {
  const chat = useInternalChat();
  const { setIsOpen, setPageActive } = chat;
  const unusedLauncherRef = useRef(null);
  useEffect(() => {
    setIsOpen(false);
    setPageActive(true);
    return () => setPageActive(false);
  }, [setIsOpen, setPageActive]);
  if (chat.enabled === null) return <Spin />;
  if (!chat.enabled) return <Empty description="Internal Chat is not enabled for your account." />;
  return <AppTheme><InternalChatPanel launcherRef={unusedLauncherRef} fullPage /></AppTheme>;
}
